/**
 * AuthRedirectHandler Component
 *
 * Handles post-authentication role-based redirection.
 * After a user successfully authenticates, this component:
 * 1. Waits for authentication to complete
 * 2. Checks for returnTo URL in sessionStorage (from cross-app login)
 * 3. If no returnTo, reads the user's roles from the ID token
 * 4. Automatically redirects to the appropriate app
 * 5. Passes ID token to downstream app for cross-origin session persistence
 *
 * Redirect logic:
 * - returnTo in sessionStorage → use that URL (cross-app login flow)
 * - Admin role → https://admin.hirenow.com
 * - User role (or default) → https://user.hirenow.com
 * 
 * Token passing:
 * - The ID token is passed via URL fragment (#token=...) to maintain
 *   session continuity across different app origins.
 */

import { useEffect, useState } from 'react';
import { useAuth, getRedirectUrlForRoles } from '../auth';
import { Loading } from '../components';

/**
 * Retrieves and clears the returnTo URL from sessionStorage.
 * Returns null if not set or invalid.
 */
function getAndClearReturnTo(): string | null {
  const returnTo = sessionStorage.getItem('auth_returnTo');
  if (returnTo) {
    sessionStorage.removeItem('auth_returnTo');
    // Validate it's a valid URL
    try {
      new URL(returnTo);
      return returnTo;
    } catch {
      console.warn('[AuthRedirectHandler] Invalid returnTo URL:', returnTo);
      return null;
    }
  }
  return null;
}

/**
 * Redirect status for tracking the redirection process
 */
type RedirectStatus =
  | 'checking' // Checking authentication status
  | 'fetching-roles' // Fetching user roles
  | 'redirecting' // About to redirect
  | 'error'; // An error occurred

/**
 * AuthRedirectHandler Component
 *
 * Automatically redirects authenticated users to the appropriate app.
 */
export function AuthRedirectHandler() {
  const { isAuthenticated, isLoading, roles, user, error } = useAuth();
  const [status, setStatus] = useState<RedirectStatus>('checking');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    // Still loading authentication state
    if (isLoading) {
      setStatus('checking');
      return;
    }

    // Not authenticated - this shouldn't happen if routing is correct
    if (!isAuthenticated) {
      setStatus('checking');
      return;
    }

    // Authentication error
    if (error) {
      setStatus('error');
      return;
    }

    // Check for returnTo URL first (cross-app login flow)
    const returnTo = getAndClearReturnTo();
    
    let targetUrl: string;
    
    if (returnTo) {
      // Use returnTo if available (user came from another app)
      targetUrl = returnTo;
      console.info('[AuthRedirectHandler] Using returnTo URL:', returnTo);
    } else {
      // Fall back to role-based redirection
      setStatus('fetching-roles');
      targetUrl = getRedirectUrlForRoles(roles);
      const isAdmin = roles.some((role) => role.toLowerCase() === 'admin');
      console.info(
        `[AuthRedirectHandler] Role-based redirect for ${user?.email ?? 'user'} to ${targetUrl}`,
        { roles, isAdmin }
      );
    }

    setRedirectUrl(targetUrl);

    // Automatic redirect with token
    setStatus('redirecting');
    if (user?.idToken) {
      const urlWithToken = `${targetUrl}#token=${encodeURIComponent(user.idToken)}`;
      window.location.replace(urlWithToken);
    } else {
      console.warn('[AuthRedirectHandler] No idToken available, redirecting without token');
      window.location.replace(targetUrl);
    }
  }, [isAuthenticated, isLoading, roles, user, error]);



  // Render appropriate UI based on status
  switch (status) {
    case 'checking':
      return (
        <Loading fullScreen message="Verifying authentication..." />
      );

    case 'fetching-roles':
      return (
        <Loading fullScreen message="Loading your profile..." />
      );

    case 'redirecting':
      return (
        <div className="redirect-container">
          <Loading message="Redirecting you..." />
          <p className="redirect-message">
            Taking you to{' '}
            <a href={redirectUrl ?? '#'} className="redirect-link">
              {redirectUrl}
            </a>
          </p>
          <p className="redirect-fallback">
            If you are not redirected automatically,{' '}
            <a href={redirectUrl ?? '#'} className="redirect-link">
              click here
            </a>
            .
          </p>
        </div>
      );

    case 'error':
      return (
        <div className="redirect-error">
          <h2>Authentication Error</h2>
          <p>{error?.message ?? 'An unexpected error occurred'}</p>
          <a href="/" className="error-back-link">
            Return to login
          </a>
        </div>
      );

    default:
      return <Loading fullScreen />;
  }
}

/**
 * ProtectedRoute Component
 *
 * A wrapper component that ensures the user is authenticated
 * before rendering children. If not authenticated, redirects to login.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Required roles to access this route (optional) */
  requiredRoles?: string[];
  /** Fallback component to show while loading */
  fallback?: React.ReactNode;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  fallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, roles } = useAuth();

  // Show loading state
  if (isLoading) {
    return <>{fallback ?? <Loading fullScreen message="Loading..." />}</>;
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Use window.location for full page navigation
    window.location.replace('/');
    return <Loading fullScreen message="Redirecting to login..." />;
  }

  // Check required roles if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((required) =>
      roles.some((role) => role.toLowerCase() === required.toLowerCase())
    );

    if (!hasRequiredRole) {
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <p>Required roles: {requiredRoles.join(', ')}</p>
        </div>
      );
    }
  }

  return <>{children}</>;
}
